import 'react-day-picker/dist/style.css';

import makeStyles from '@mui/styles/makeStyles';
import { useParams } from 'react-router-dom';

import type { Unit } from '@/Units/types';

import { useBills } from '../../Billing/useBills';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import type { PmsConfig, PmsType } from '../types';
import { useStructure } from '../useStructures';
import { Billing } from '../View/Billing';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    width: '100%',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  header: {
    alignItems: 'baseline',
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: 0,
    paddingLeft: 0,
    textAlign: 'left',
  },
  chartBody: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  chartLoader: {
    left: '20%',
    position: 'absolute',
    top: '50%',
    width: '60%',
  },
  selectedDays: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.primary.main,
  },
  editIcon: {
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(-0.5),
    opacity: 0.3,
    '&:hover': {
      opacity: 1,
    },
  },
  prevNextButton: {
    height: 'fit-content',
  },
  nextButton: {
    marginLeft: theme.spacing(1),
  },
  prevButton: {
    marginRight: theme.spacing(1),
  },
}));

interface IProps {
  stripeConnect: boolean;
  payOnlineUrl?: string | null;
  paymentInstructions?: string | null;
  onPaymentSettingsUpdate?: (
    payOnlineUrl: string | null,
    paymentInstructions: string | null,
    dueDateDays: number | null,
  ) => Promise<void>;
  pmsType?: PmsType | null;
  pmsConfig?: PmsConfig | null;
  onPmsSettingsUpdate?: (pmsType: PmsType | null, pmsConfig: PmsConfig | null) => Promise<void>;
  unitPmsNames?: Record<string, string>;
  unitPmsIds?: Record<string, number | string>;
  structureUnits?: Unit[];
  onStructureRefresh?: () => void;
}

export const UtilityWaterBills = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const { structureId } = useParams();
  if (!structureId) {
    throw new Error('MISSING_REQUIRED_ROUTE_DATA');
  }

  // Data fetching ////////////////////

  const { structure } = useStructure(structureId);

  // Load bills
  const { bills = [], isLoading } = useBills(structureId);

  return (
    <ErrorBoundary>
      <div data-component-id={'STRUCTURE-VIEW'} className={classes.wrapper}>
        {structure && (
          <div className={classes.container}>
            <div className={classes.header}>
              <div style={{ flexGrow: 1 }}>
                <Billing
                  structureId={structureId}
                  structureName={structure.name}
                  bills={bills}
                  isLoading={isLoading}
                  defaultRateMethod={structure.rate_method}
                  structureAddress={structure.address}
                  timeZone={structure.timezone_key}
                  stripeConnect={props.stripeConnect}
                  units={structure.units}
                  volumeUnits={structure.volume_units}
                  payOnlineUrl={props.payOnlineUrl}
                  paymentInstructions={props.paymentInstructions}
                  dueDateDays={structure.due_date_days}
                  onPaymentSettingsUpdate={props.onPaymentSettingsUpdate}
                  pmsType={props.pmsType}
                  pmsConfig={props.pmsConfig}
                  onPmsSettingsUpdate={props.onPmsSettingsUpdate}
                  unitPmsNames={props.unitPmsNames}
                  unitPmsIds={props.unitPmsIds}
                  structureUnits={props.structureUnits}
                  onStructureRefresh={props.onStructureRefresh}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};
